import { Check, Circle } from '@plataforma/ui/iconos';
import type { Sitio } from './comercial-comun';

interface Paso {
  hecho: boolean;
  texto: string;
  ayuda: string;
  ancla: string;
}

/** Lo que hace que una tienda se vea completa aunque no tenga inventario (arquitectura 2.19.2). */
export function pasosDe(s: Sitio): Paso[] {
  const i = s.identidad ?? {};
  return [
    {
      hecho: !!s.logoUrl,
      texto: 'Tu logotipo',
      ayuda: 'Súbelo desde tu teléfono.',
      ancla: 'datos',
    },
    {
      hecho: !!s.portadaUrl,
      texto: 'Foto de portada',
      ayuda: 'La fachada o tu mejor auto.',
      ancla: 'datos',
    },
    {
      hecho: (s.galeriaIds?.length ?? 0) >= 3,
      texto: 'Tres fotos del negocio',
      ayuda: 'El taller, el patio, tu equipo trabajando.',
      ancla: 'datos',
    },
    {
      hecho: !!(s.whatsapp || s.telefono),
      texto: 'WhatsApp o teléfono',
      ayuda: 'Por donde te escriben los compradores.',
      ancla: 'datos',
    },
    {
      hecho: !!(s.horario && s.direccion),
      texto: 'Horario y dirección',
      ayuda: 'Para que sepan cuándo y dónde encontrarte.',
      ancla: 'datos',
    },
    {
      hecho: !!i.quienesSomos,
      texto: 'Quiénes son',
      ayuda: 'Una o dos frases.',
      ancla: 'identidad',
    },
    {
      hecho: !!i.historia,
      texto: 'Tu historia',
      ayuda: 'Cómo empezaste y cómo trabajas.',
      ancla: 'identidad',
    },
    {
      hecho: (i.servicios?.length ?? 0) > 0,
      texto: 'Lo que hacen',
      ayuda: 'Tus servicios o especialidades.',
      ancla: 'identidad',
    },
    {
      hecho: (i.senales?.length ?? 0) > 0 || !!i.fundadoEn,
      texto: 'Por qué confiar en ti',
      ayuda: 'Pedimento, garantía, años en el oficio.',
      ancla: 'identidad',
    },
    {
      hecho: !!s.plantilla,
      texto: 'Elegir tu plantilla',
      ayuda: 'Cómo se ve tu tienda.',
      ancla: 'plantilla',
    },
    {
      hecho: s.publicado,
      texto: 'Publicar tu tienda',
      ayuda: 'Que la vea todo mundo.',
      ancla: 'datos',
    },
  ];
}

/**
 * Completa tu tienda (arquitectura 2.19.2, I4): una guía con avance arriba de Admin › Mi sitio.
 * Cada paso lleva a donde se completa; desaparece cuando todo está listo.
 */
export function CompletaTienda({ sitio }: { sitio: Sitio }) {
  const pasos = pasosDe(sitio);
  const hechos = pasos.filter((p) => p.hecho).length;
  const pct = Math.round((hechos / pasos.length) * 100);
  if (hechos === pasos.length) return null;
  const siguiente = pasos.find((p) => !p.hecho);
  return (
    <section className="seccion cuerpo completa-tienda" aria-labelledby="t-completa">
      <div className="completa-cabeza">
        <div>
          <h2 id="t-completa">Completa tu tienda</h2>
          <p className="subtitulo">
            Una tienda completa vende aunque todavía no tengas autos publicados.
            {siguiente && (
              <>
                {' '}
                Sigue: <a href={`#${siguiente.ancla}`}>{siguiente.texto.toLowerCase()}</a>.
              </>
            )}
          </p>
        </div>
        <strong className="completa-pct">{pct}%</strong>
      </div>
      <div
        className="completa-barra"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Avance de tu tienda"
      >
        <i style={{ width: `${pct}%` }} />
      </div>
      <ol className="completa-pasos">
        {pasos.map((p) => (
          <li key={p.texto} data-hecho={p.hecho}>
            {p.hecho ? (
              <Check size={16} aria-hidden="true" />
            ) : (
              <Circle size={16} aria-hidden="true" />
            )}
            <a href={`#${p.ancla}`}>
              <b>{p.texto}</b>
              {!p.hecho && <small>{p.ayuda}</small>}
            </a>
          </li>
        ))}
      </ol>
    </section>
  );
}
